// The simulation core: one world, its clock, its command log and the flow network, with no DOM or
// three.js. The game (and the debug page) feed it real frame times and commands, and read its report.
import { Clock } from './clock';
import { History, parseCommand, type Command, type Outcome } from './commands';
import type { GameData } from './data';
import { step, TICK_MINUTES, TICK_MS, type TickFigures } from './flow';
import { buildNetwork, type Network } from './network';
import { list, record, text } from './validate';
import { createWorld, type Store, type World } from './world';

/** Deliveries this close to an order's amount complete it, so rounding never leaves 49.999… of 50. */
const ORDER_TOLERANCE = 1e-6;

export interface Scenario {
  readonly name: string;
  readonly commands: readonly Command[];
}

/** Reads a scenario file: a name and the commands that build it from a fresh game. */
export function parseScenario(raw: unknown): Scenario {
  const v = record(raw, 'scenario');
  return { name: text(v.name, 'scenario.name'), commands: list(v.commands, 'scenario.commands').map((c, i) => parseCommand(c, `scenario.commands[${i}]`)) };
}

export class Simulation {
  readonly clock = new Clock();
  readonly history = new History();
  private network: Network;
  private latest: TickFigures | undefined;

  constructor(
    readonly data: GameData,
    readonly world: World = createWorld(data),
  ) {
    this.network = buildNetwork(world);
  }

  /** A fresh game with a scenario's commands already applied; they are not in the undo log. */
  static withScenario(data: GameData, scenario: Scenario): Simulation {
    const sim = new Simulation(data);
    scenario.commands.forEach((command, i) => {
      const outcome = sim.execute(command);
      if (!outcome.ok) throw new Error(`Escenario «${scenario.name}», orden ${i + 1}: ${outcome.reason}`);
    });
    sim.history.clear();
    return sim;
  }

  /** Game time in milliseconds. */
  get time(): number {
    return this.world.tick * TICK_MS;
  }

  /** Advances by a frame of real time at the clock's speed; returns the ticks run. */
  frame(realMs: number): number {
    const ticks = this.clock.advance(realMs);
    this.run(ticks);
    return ticks;
  }

  /** Runs ticks straight away, whatever the clock says: fast-forward. */
  run(ticks: number): void {
    for (let i = 0; i < ticks; i++) this.tick();
  }

  /** Fast-forwards a span of game time. */
  runFor(gameMs: number): void {
    this.run(Math.round(gameMs / TICK_MS));
  }

  execute(command: Command): Outcome {
    const outcome = this.history.execute(this.world, command);
    if (outcome.ok) this.latest = undefined;
    return outcome;
  }

  undo(): string | undefined {
    const label = this.history.undo(this.world);
    if (label) this.latest = undefined;
    return label;
  }

  redo(): string | undefined {
    const label = this.history.redo(this.world);
    if (label) this.latest = undefined;
    return label;
  }

  /**
   * Rates per minute of every line, as of the last tick. After a layout change, or before the first
   * tick, they are worked out without moving anything, so they are right even while paused.
   */
  get figures(): TickFigures {
    this.latest ??= step(this.flowNetwork, this.stores(), false);
    return this.latest;
  }

  /** The flow network of the current layout. */
  get flowNetwork(): Network {
    if (this.network.layoutVersion !== this.world.layoutVersion) this.network = buildNetwork(this.world);
    return this.network;
  }

  private *stores(): Iterable<Store> {
    for (const tile of this.world.tiles.values()) yield* tile.stores.values();
  }

  private tick(): void {
    const figures = step(this.flowNetwork, this.stores(), true);
    this.world.tick++;
    for (const order of this.world.orders) {
      if (order.doneAt !== undefined) continue;
      for (const tile of this.world.tiles.values()) {
        if (tile.def.kind !== 'warehouse') continue;
        for (const store of tile.stores.values()) order.delivered += (figures.stores.get(store)?.in.get(order.def.item.id) ?? 0) * TICK_MINUTES;
      }
      if (order.delivered >= order.def.amount - ORDER_TOLERANCE) {
        order.delivered = order.def.amount;
        order.doneAt = this.world.tick;
        this.world.coins += order.def.reward;
      }
    }
    this.latest = figures;
  }
}
